import React, {
    KeyboardEvent,
    ReactNode,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";

import "./AppMultiSelect_New.scss";

export interface AppMultiSelectOption {
    value: string;
    label: ReactNode;
    searchText?: string;
    disabled?: boolean;
}

export interface AppMultiSelect_NewProps {
    options: AppMultiSelectOption[];
    value?: string[];
    defaultValue?: string[];
    onChange?: (values: string[], selectedOptions: AppMultiSelectOption[]) => void;
    label?: ReactNode;
    name?: string;
    controlId?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    noOptionsMessage?: ReactNode;
    helperText?: ReactNode;
    error?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    clearable?: boolean;
    searchable?: boolean;
    closeOnSelect?: boolean;
    hideSelectedOptions?: boolean;
    maxSelections?: number;
    className?: string;
}

const optionText = (option: AppMultiSelectOption) =>
    option.searchText ?? (typeof option.label === "string" ? option.label : option.value);

const AppMultiSelect_New: React.FC<AppMultiSelect_NewProps> = ({
    options,
    value,
    defaultValue = [],
    onChange,
    label,
    name,
    controlId,
    placeholder = "Select options",
    searchPlaceholder = "Type to search",
    noOptionsMessage = "No options found.",
    helperText,
    error,
    required = false,
    disabled = false,
    clearable = true,
    searchable = true,
    closeOnSelect = false,
    hideSelectedOptions = true,
    maxSelections,
    className = "",
}) => {
    const generatedId = useId();
    const id = controlId ?? name ?? `app-multi-select-${generatedId}`;
    const listboxId = `${id}-listbox`;
    const messageId = `${id}-message`;
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [internalValue, setInternalValue] = useState<string[]>(defaultValue);
    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const selectedValues = value ?? internalValue;
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    const selectedOptions = useMemo(
        () => selectedValues
            .map((selectedValue) => options.find((option) => option.value === selectedValue))
            .filter((option): option is AppMultiSelectOption => Boolean(option)),
        [options, selectedValues]
    );

    const visibleOptions = useMemo(() => {
        const normalizedQuery = query.trim().toLocaleLowerCase();
        return options.filter((option) => {
            if (hideSelectedOptions && selectedSet.has(option.value)) return false;
            return !normalizedQuery || optionText(option).toLocaleLowerCase().includes(normalizedQuery);
        });
    }, [hideSelectedOptions, options, query, selectedSet]);

    useEffect(() => {
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, []);

    useEffect(() => {
        setActiveIndex((current) => Math.min(current, Math.max(visibleOptions.length - 1, 0)));
    }, [visibleOptions.length]);

    const commitValue = (nextValues: string[]) => {
        const uniqueValues = Array.from(new Set(nextValues));
        if (value === undefined) setInternalValue(uniqueValues);
        onChange?.(
            uniqueValues,
            uniqueValues
                .map((selectedValue) => options.find((option) => option.value === selectedValue))
                .filter((option): option is AppMultiSelectOption => Boolean(option))
        );
    };

    const removeValue = (selectedValue: string) => {
        if (disabled) return;
        commitValue(selectedValues.filter((item) => item !== selectedValue));
        inputRef.current?.focus();
    };

    const selectOption = (option: AppMultiSelectOption) => {
        if (disabled || option.disabled) return;
        const alreadySelected = selectedSet.has(option.value);
        if (!alreadySelected && maxSelections !== undefined && selectedValues.length >= maxSelections) return;
        commitValue(
            alreadySelected
                ? selectedValues.filter((item) => item !== option.value)
                : [...selectedValues, option.value]
        );
        setQuery("");
        setActiveIndex(0);
        if (closeOnSelect) setIsOpen(false);
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Backspace" && !query && selectedValues.length > 0) {
            removeValue(selectedValues[selectedValues.length - 1]);
            return;
        }
        if (event.key === "Escape") {
            setIsOpen(false);
            return;
        }
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setIsOpen(true);
            const direction = event.key === "ArrowDown" ? 1 : -1;
            setActiveIndex((current) => {
                const length = visibleOptions.length;
                return length ? (current + direction + length) % length : 0;
            });
            return;
        }
        if (event.key === "Enter" && isOpen && visibleOptions[activeIndex]) {
            event.preventDefault();
            selectOption(visibleOptions[activeIndex]);
        }
    };

    const activeOption = visibleOptions[activeIndex];
    const atLimit = maxSelections !== undefined && selectedValues.length >= maxSelections;

    return (
        <div
            ref={rootRef}
            className={`app-multi-select${error ? " app-multi-select--error" : ""}${disabled ? " app-multi-select--disabled" : ""} ${className}`.trim()}
        >
            {label && (
                <label className="app-multi-select__label" htmlFor={id}>
                    {label}
                    {required && <span className="app-multi-select__required" aria-hidden="true"> *</span>}
                </label>
            )}

            <div
                className={`app-multi-select__control${isOpen ? " is-open" : ""}`}
                onClick={() => {
                    if (!disabled) {
                        setIsOpen(true);
                        inputRef.current?.focus();
                    }
                }}
            >
                <div className="app-multi-select__values">
                    {selectedOptions.map((option) => (
                        <span className="app-multi-select__chip" key={option.value}>
                            <span>{option.label}</span>
                            <button
                                type="button"
                                aria-label={`Remove ${optionText(option)}`}
                                disabled={disabled}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    removeValue(option.value);
                                }}
                            >
                                {"\u00D7"}
                            </button>
                        </span>
                    ))}

                    <input
                        ref={inputRef}
                        id={id}
                        type="text"
                        role="combobox"
                        autoComplete="off"
                        value={query}
                        disabled={disabled}
                        readOnly={!searchable}
                        required={required && selectedValues.length === 0}
                        aria-required={required}
                        aria-invalid={error ? true : undefined}
                        aria-expanded={isOpen}
                        aria-controls={listboxId}
                        aria-activedescendant={isOpen && activeOption ? `${id}-option-${activeOption.value}` : undefined}
                        aria-describedby={error || helperText ? messageId : undefined}
                        placeholder={selectedValues.length === 0
                            ? placeholder
                            : isOpen && searchable
                                ? searchPlaceholder
                                : ""}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setIsOpen(true);
                            setActiveIndex(0);
                        }}
                        onFocus={() => !disabled && setIsOpen(true)}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                <div className="app-multi-select__actions">
                    {clearable && selectedValues.length > 0 && !disabled && (
                        <button
                            type="button"
                            className="app-multi-select__clear"
                            aria-label="Clear all selected options"
                            onClick={(event) => {
                                event.stopPropagation();
                                commitValue([]);
                                setQuery("");
                            }}
                        >
                            {"\u00D7"}
                        </button>
                    )}
                    <span className="app-multi-select__separator" />
                    <button
                        type="button"
                        className="app-multi-select__toggle"
                        aria-label={isOpen ? "Close options" : "Open options"}
                        disabled={disabled}
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsOpen((current) => !current);
                            inputRef.current?.focus();
                        }}
                    >
                        <span className={isOpen ? "is-open" : undefined} />
                    </button>
                </div>
            </div>

            {isOpen && !disabled && (
                <div className="app-multi-select__menu" id={listboxId} role="listbox" aria-multiselectable="true">
                    {visibleOptions.length > 0 ? visibleOptions.map((option, index) => {
                        const selected = selectedSet.has(option.value);
                        const unavailable = option.disabled || (!selected && atLimit);
                        return (
                            <div
                                id={`${id}-option-${option.value}`}
                                key={option.value}
                                role="option"
                                aria-selected={selected}
                                aria-disabled={unavailable}
                                className={`app-multi-select__option${index === activeIndex ? " is-active" : ""}${selected ? " is-selected" : ""}${unavailable ? " is-disabled" : ""}`}
                                onMouseEnter={() => setActiveIndex(index)}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => !unavailable && selectOption(option)}
                            >
                                {option.label}
                                {selected && <span aria-hidden="true">{"\u2713"}</span>}
                            </div>
                        );
                    }) : (
                        <div className="app-multi-select__empty">{noOptionsMessage}</div>
                    )}
                </div>
            )}

            {(error || helperText) && (
                <div id={messageId} className={error ? "app-multi-select__error" : "app-multi-select__helper"}>
                    {error || helperText}
                </div>
            )}

            {name && selectedValues.map((selectedValue) => (
                <input key={selectedValue} type="hidden" name={name} value={selectedValue} />
            ))}
        </div>
    );
};

export default AppMultiSelect_New;
